import { ParlorDetailProps } from "@/types/parlor";

import { GUEST_TO_PYEONG } from "./recommendBinso";

/**
 * E5 금액 계산. 모두 「장례식장에 내는 돈」 만(시설 · 빈소 · 식사 · 제단꽃), e하늘 공시 요금 기준(2026-11-05 주완).
 * 서버 expected_charge_sum.일반장(= 무빈소 + 2 x 빈소 + 591만 원 고정분)은 쓰지 않는다. 591만 원은 장례식장 공시 요금이 아닌
 * 고정분이라(무엇이 들었는지 서버 확인 전) 첫 화면 금액이 다음 화면들보다 크게 나왔다.
 * 첫 화면 「조문객 100명 · 3일장 기준」 은 아래 getGuestCostRange 의 「100명 내외」 범위를 그대로 쓴다.
 */

const toManwon = (won: number) => Math.floor(won / 10000);

// ---------------------------------------------------------------------------
// 조문객 질문(E5 P3) 범위 바
// ---------------------------------------------------------------------------

/** 조문객 선택지별 범위의 최소 폭(만 원) */
const MIN_RANGE_MANWON = 200;

/** 기존 견적 body 의 condolers 값(조문객예상인원_LIST)을 그대로 쓴다 */
export type GuestRangeKey =
  | "0명(무빈소)"
  | "50명 내외"
  | "100명 내외"
  | "200명 이상"
  | "모르겠어요";

export interface GuestCostRange {
  /** 이 장례식장에서 가장 적게 ~ 가장 많이(만 원). 막대 양 끝 */
  minManwon: number;
  maxManwon: number;
  /** 선택지별 예상 범위(만 원). 계산이 안 되는 선택지는 없다 */
  byGuest: Record<GuestRangeKey, { lo: number; hi: number }>;
}

/** 1인 1식 음식값이 없을 때(전국 가운데값, goi-search 2026-11-03 장례식장별 1인식비 문서 a2) */
const FALLBACK_FOOD_PER_PERSON_WON = 19_626;
/**
 * 1인 1식 바구니값 → 조문객 1명이 3일 동안 쓰는 음식 · 음료 비용 배수.
 * 녹음 통화 7번 「아산 150명 음식 400~450만 원」 ÷ (아산 1인 19,730원 x 150명 = 296만) = 1.35~1.52
 */
const FOOD_MULTIPLIER = { lo: 1.35, hi: 1.5 };
/** 음식은 조문객이 있으면 무조건 든다(주완: 최소 50만 원) */
const MIN_FOOD_WON = 500_000;
/** 제단꽃 공시가가 없을 때(e하늘 제단 최저가 316곳 가운데값) */
const FALLBACK_ALTAR_WON = 450_000;
/**
 * 빈소 없이(조문객 없이) 장례식장 비용(만 원). 장례식장마다 다르게(2026-11-04 주완).
 * - 아래쪽: 그 장례식장 공시 요금 계산값(서버 expected_charge_sum.무빈소). 다만 운영 최저 70만 원 아래로는 내리지 않는다
 * - 위쪽: 계산값 x 3. 운영 메모 실측이 계산값보다 최대 2.9배 비쌌다
 *   (안양메트로 103 → 300 · 가평군농협 109 → 270 · 쉴낙원 오산동탄 79 → 150 · 상계백 84 → 130,
 *    wiki/features/mubinso-region-availability.md 조건 4). 네 곳 실측이 모두 이 범위 안에 든다
 * - 서버 값이 없으면 FALLBACK
 */
const NO_BINSO_FLOOR_MANWON = 70;
const NO_BINSO_HI_MULTIPLIER = 3;
const NO_BINSO_FALLBACK = { lo: 130, hi: 300 };
const getNoBinsoRange = (parlor: ParlorDetailProps): { lo: number; hi: number } => {
  const won = parlor?.expected_charge_sum?.["무빈소"];
  if (!won) return NO_BINSO_FALLBACK;
  const lo = Math.max(NO_BINSO_FLOOR_MANWON, Math.floor(won / 100000) * 10);
  const hi = Math.max(lo + 100, Math.ceil((won * NO_BINSO_HI_MULTIPLIER) / 100000) * 10);
  return { lo, hi };
};
/** 빈소를 차리면 시설 + 음식 + 제단으로 보통 300~400만 원, 가장 싼 창녕도 300만 원 안쪽(운영, 2026-11-03 주완) */
const MIN_WITH_BINSO_MANWON = 300;

/**
 * 조문객 수별 「장례식장에 내는 돈」 3일장 예상 범위(만 원). 고이 상품 · 상조 · 화장 비용은 넣지 않는다(2026-11-03 주완).
 *
 *   장례식장 비용 = 시설(안치료 2일 + 수시비 + 입관실 + 쓰레기 수거) + 조문객에 맞는 빈소 2일
 *                 + 제단꽃(가장 싼 기본) + 음식(조문객 x 이 장례식장 1인 1식 값 x 1.35~1.5, 최소 50만 원)
 *   빈소      = 조문객 x 0.44평 이상인 빈소 중 가장 작은 것(없으면 가장 큰 빈소)
 *   하한      = 빈소를 차리면 300만 원. 조문객 없이(무빈소)는 운영 실측 130~300만 원(임시값).
 *               폭이 200만 원보다 좁으면 위쪽으로만 넓힌다(아래로 넓히면 너무 싸 보인다)
 *
 * 서버 expected_charge_sum 은 쓰지 않는다. 591만 원 · 20만 원 고정분에 장례식장 밖 비용이 섞여 있어서다.
 */
export function getGuestCostRange(
  parlor: ParlorDetailProps,
  foodPerPersonWon: number | null,
  altarWon: number | null,
): GuestCostRange | null {
  // 3일장: 빈소 · 안치실을 2일 쓴다
  const useDays = 2;
  const perPerson = foodPerPersonWon ?? FALLBACK_FOOD_PER_PERSON_WON;
  const altar = altarWon ?? FALLBACK_ALTAR_WON;
  const services = parlor?.services ?? {};
  const fee = (key: string) => services?.[key]?.charge ?? 0;
  const facilityWon =
    useDays * fee("안치료") + fee("수시비") + fee("입관실") + fee("쓰레기 수거료");
  const binsos = (parlor?.binsos ?? []).filter(
    (b) => !!b.pyeong && typeof b.charge === "number" && b.charge > 0,
  );
  if (binsos.length === 0) return null;

  const binsoFor = (guests: number) => {
    const need = guests * GUEST_TO_PYEONG;
    const fits = binsos
      .filter((b) => (b.pyeong ?? 0) >= need)
      .sort((a, b) => (a.pyeong ?? 0) - (b.pyeong ?? 0) || a.charge - b.charge);
    return fits[0] ?? [...binsos].sort((a, b) => (b.pyeong ?? 0) - (a.pyeong ?? 0))[0];
  };
  const totalWon = (guests: number, multiplier: number) =>
    facilityWon +
    binsoFor(guests).charge * useDays +
    altar +
    Math.max(MIN_FOOD_WON, guests * perPerson * multiplier);
  const range = (from: number, to: number) => {
    const lo = Math.max(
      MIN_WITH_BINSO_MANWON,
      Math.floor(toManwon(totalWon(from, FOOD_MULTIPLIER.lo)) / 10) * 10,
    );
    const hi = Math.max(
      lo + MIN_RANGE_MANWON,
      Math.ceil(toManwon(totalWon(to, FOOD_MULTIPLIER.hi)) / 10) * 10,
    );
    return { lo, hi };
  };

  // 조문객 없이(무빈소): 장례식장별 공시 계산값 ~ 그 3배(실측 차이 반영)
  const noGuest = getNoBinsoRange(parlor);
  const byGuest = {
    "0명(무빈소)": noGuest,
    // 「100명 안팎」 은 100~200명까지 올 수 있다(주완). 위쪽 끝을 다음 단계 인원으로 잡는다
    "50명 내외": range(50, 100),
    "100명 내외": range(100, 200),
    // 모르면 가장 많이 고르는 100명 기준으로 보여 준다(리드의 42%, 2026-11-05 주완). 견적에는 「모르겠어요」 그대로 넘긴다
    모르겠어요: range(100, 200),
    "200명 이상": range(200, 300),
  };
  // 막대 최저 = 이 장례식장 무빈소 범위 시작
  return {
    minManwon: noGuest.lo,
    maxManwon: Math.max(...Object.values(byGuest).map((r) => r.hi)),
    byGuest,
  };
}

// ---------------------------------------------------------------------------
// E5 ④ 비용 구성 항목
// ---------------------------------------------------------------------------

/** 비용 구성 항목(식사 · 빈소 · 제단꽃 · 기타(안치 · 입관)) */
export type CostItemKey = "food" | "binso" | "altar" | "base";

/** 선택지별 인원 범위(범위 계산과 같은 기준) */
const GUEST_BAND: Record<GuestRangeKey, [number, number]> = {
  "0명(무빈소)": [0, 0],
  "50명 내외": [50, 100],
  "100명 내외": [100, 200],
  "200명 이상": [200, 300],
  모르겠어요: [100, 200],
};

/** 제단꽃 등급 폭이 없을 때(e하늘 316곳: 가장 싼 것 ~ 가운데값 차이의 대략) */
const FALLBACK_ALTAR_SPREAD_WON = 300_000;

// ---------------------------------------------------------------------------
// E5 ④ 「우리 가족에 맞게 바꿔 보기」: 조문객 수 · 빈소 크기를 고객이 직접 바꾸면 예상 비용이 움직인다
// ---------------------------------------------------------------------------

export interface CostConfigItem {
  key: CostItemKey;
  /** 0~1. 지금 설정의 예상 비용 안에서 이 항목 몫(가운데값 기준). 막대 조각 길이에만 쓴다 */
  share: number;
  /** 조문객이 없어 필요 없는 항목(식사 · 빈소 · 제단꽃) */
  off: boolean;
}

export interface CostConfig {
  /** 지금 설정의 예상 범위(만 원) */
  lo: number;
  hi: number;
  items: CostConfigItem[];
  /** 빈소 고르기. 조문객이 없거나 공시 빈소가 없으면 null */
  binso: {
    /** 고를 수 있는 평형(작은 것부터, 같은 평형은 싼 것 하나) */
    pyeongs: number[];
    /** 지금 보여 주는 평형 */
    pyeong: number;
    /** 조문객 수에 맞는 추천 평형 */
    recommended: number;
  } | null;
  /** 제단꽃 등급. 조문객이 없으면 null */
  altar: { labels: string[]; index: number } | null;
  /** 식사 인분. 조문객 수에서 시작해 30인분씩 바꾼다. 조문객이 없으면 null */
  food: { servings: number; base: number } | null;
}

/** 식사를 한 번에 늘리고 줄이는 양(인분, 주완) */
export const FOOD_STEP_SERVINGS = 30;

/** 제단꽃 등급 이름(공시가 가장 싼 것 · 가운데 · 위쪽 4분의 1). 수연 님 「기본 · 특 · VIP」(11-05 주완) */
const ALTAR_GRADE_LABELS = ["기본", "특", "VIP"];

/**
 * 조문객 수와 (고객이 바꾼) 빈소 평형 · 제단꽃 등급으로 예상 범위와 항목 몫을 다시 계산한다. 식은 getGuestCostRange 와 같다.
 * 아무것도 바꾸지 않았으면 질문 화면 범위와 같은 값이 나온다(빈소 아래쪽은 추천, 위쪽은 다음 인원에 맞는 빈소, 꽃은 기본).
 * 빈소를 고르면 위 · 아래 모두 그 빈소로 계산한다. 조문객이 없으면 무빈소 범위, 식사 · 빈소 · 제단꽃은 off.
 */
export function getCostConfig(
  parlor: ParlorDetailProps,
  foodPerPersonWon: number | null,
  altarGradesWon: number[] | null,
  guest: GuestRangeKey,
  binsoPyeong: number | null,
  altarIndex: number,
  foodServings: number | null,
): CostConfig | null {
  const [from, to] = GUEST_BAND[guest];
  const fee = (k: string) => parlor?.services?.[k]?.charge ?? 0;
  const facilityWon = 2 * fee("안치료") + fee("수시비") + fee("입관실") + fee("쓰레기 수거료");
  const ITEMS = (["food", "binso", "altar", "base"] as const).map((key) => ({ key }));

  if (from === 0) {
    const r = getNoBinsoRange(parlor);
    return {
      ...r,
      items: ITEMS.map((i) => ({ ...i, share: i.key === "base" ? 1 : 0, off: i.key !== "base" })),
      binso: null,
      altar: null,
      food: null,
    };
  }

  const priced = (parlor?.binsos ?? []).filter(
    (b) => !!b.pyeong && typeof b.charge === "number" && b.charge > 0,
  );
  if (priced.length === 0) return null;
  // 평형마다 가장 싼 빈소 하나
  const byPyeong = new Map<number, number>();
  priced.forEach((b) => {
    const p = Math.round(b.pyeong ?? 0);
    const prev = byPyeong.get(p);
    if (prev === undefined || b.charge < prev) byPyeong.set(p, b.charge);
  });
  const pyeongs = Array.from(byPyeong.keys()).sort((a, b) => a - b);
  const fitFor = (guests: number) =>
    pyeongs.find((p) => p >= guests * GUEST_TO_PYEONG) ?? pyeongs[pyeongs.length - 1];
  const recommended = fitFor(from);
  const chosen = binsoPyeong !== null && byPyeong.has(binsoPyeong) ? binsoPyeong : null;
  const binsoLo = byPyeong.get(chosen ?? recommended) ?? 0;
  const binsoHi = byPyeong.get(chosen ?? fitFor(to)) ?? 0;

  const perPerson = foodPerPersonWon ?? FALLBACK_FOOD_PER_PERSON_WON;
  const grades = altarGradesWon?.length
    ? altarGradesWon
    : [FALLBACK_ALTAR_WON, FALLBACK_ALTAR_WON + FALLBACK_ALTAR_SPREAD_WON];
  const gradeIndex = Math.min(Math.max(altarIndex, 0), grades.length - 1);
  const altar = grades[gradeIndex];
  // 인분을 바꾸면 범위 양 끝을 같은 비율로 옮긴다(늘리면 위 · 아래 모두 오른다)
  const servings = Math.max(10, foodServings ?? from);
  const scale = servings / from;
  const foodLo = Math.max(MIN_FOOD_WON, servings * perPerson * FOOD_MULTIPLIER.lo);
  const foodHi = Math.max(MIN_FOOD_WON, to * scale * perPerson * FOOD_MULTIPLIER.hi);
  const lo = Math.max(
    MIN_WITH_BINSO_MANWON,
    Math.floor(toManwon(facilityWon + binsoLo * 2 + altar + foodLo) / 10) * 10,
  );
  const hi = Math.max(
    lo + MIN_RANGE_MANWON,
    Math.ceil(toManwon(facilityWon + binsoHi * 2 + altar + foodHi) / 10) * 10,
  );

  const mids: Record<CostItemKey, number> = {
    food: (foodLo + foodHi) / 2,
    binso: binsoLo + binsoHi,
    altar,
    base: facilityWon,
  };
  const total = Object.values(mids).reduce((s, v) => s + v, 0) || 1;
  return {
    lo,
    hi,
    items: ITEMS.map((i) => ({ ...i, share: mids[i.key] / total, off: false })),
    binso: { pyeongs, pyeong: chosen ?? recommended, recommended },
    altar: { labels: ALTAR_GRADE_LABELS.slice(0, grades.length), index: gradeIndex },
    food: { servings, base: from },
  };
}
